/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LanguageIcon from '@mui/icons-material/Language';
import { IconButton } from '@mui/material';

import { DefaultLanguage, Language } from 'shared';

import { StyledMenu, StyledMenuItem } from './LanguagePicker.styles';

export type LanguageMenuProps = {
  id: string;
  anchorEl: HTMLElement | null;
  handleSetLang: (language: Language) => void;
  handleLangMenuClose: () => void;
  currentLang: string;
};

const LanguageMenu = memo<LanguageMenuProps>(props => {
  return (
    <StyledMenu
      id={props.id}
      anchorEl={props.anchorEl}
      keepMounted
      open={Boolean(props.anchorEl)}
      onClose={props.handleLangMenuClose}
    >
      <StyledMenuItem
        className={cx({ active: DefaultLanguage === props.currentLang })}
        onClick={() => props.handleSetLang(DefaultLanguage)}
      >
        <span role="img" aria-label="flag" className={'flag'}>
          🇺🇸
        </span>
      </StyledMenuItem>
      <StyledMenuItem className={cx({ active: 'de' === props.currentLang })} onClick={() => props.handleSetLang('de')}>
        <span role="img" aria-label="flag" className={'flag'}>
          🇩🇪
        </span>
      </StyledMenuItem>
    </StyledMenu>
  );
});

export const LanguagePicker = memo(() => {
  const [langEl, setLangEl] = useState<null | HTMLElement>(null);
  const { i18n } = useTranslation();

  const handleSetLang = (lang: string) => {
    handleLangMenuClose();
    void i18n.changeLanguage(lang);
  };

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangEl(null);
  };

  const renderLangMenu = () => (
    <LanguageMenu
      currentLang={i18n.language}
      id={'primary-language-menu'}
      anchorEl={langEl}
      handleSetLang={handleSetLang}
      handleLangMenuClose={handleLangMenuClose}
    />
  );

  return (
    <>
      <IconButton
        edge="end"
        aria-label="Change Language"
        aria-haspopup="true"
        onClick={handleLangMenuOpen}
        size="large"
      >
        <LanguageIcon />
      </IconButton>
      {renderLangMenu()}
    </>
  );
});
